<i18n>
{
  "en": {
    "add": "Add event type",
    "close": "Close list",
    "eventTypes": "Event types",
    "deleteEventTypeTitle": "Do you want to delete event type: \"{0}\" ?",
    "deleteEventTypeDescription": "This can not be undone",
    "delete": "Delete",
    "createEventTypeError": "Error creating event type. Please, try again later.",
    "createEventTypeSuccess": "Created event type \"{0}\" !",
    "editEventTypeSuccess": "Event type \"{0}\" was successfully edited!",
    "editEventTypeError": "Error editing event type \"{0}\". Please, try again later.",
    "deleteEventTypeSuccess": "Event type \"{0}\" was deleted",
    "deleteEventTypeError": "Error deleting event type \"{0}\". Please, try again later."
  },
  "es": {
    "add": "Añadir tipo",
    "close": "Cerrar lista",
    "eventTypes": "Tipos de evento",
    "deleteEventTypeTitle": "¿Desea borrar el tipo de evento \"{0}\" ?",
    "deleteEventTypeDescription": "Este cambio es irreversible",
    "delete": "Eliminar",
    "createEventTypeError": "Error creando el event. Inténtelo de nuevo más tarde.",
    "createEventTypeSuccess": "Creado el tipo de evento \"{0}\" !",
    "editEventTypeSuccess": "El tipo de evento \"{0}\" se editó correctamente",
    "editEventTypeError": "Error editando el tipo de evento \"{0}\". Inténtelo de nuevo más tarde.",
    "deleteEventTypeSuccess": "El tipo de evento \"{0}\" fue borrado",
    "deleteEventTypeError": "Error al borrar el tipo de evento \"{0}\". Inténtelo de nuevo más tarde."
  }
}
</i18n>
<template>
  <v-dialog v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" scrollable persistent class="px-7 py-4">
      <v-row mb-3 class="d-flex justify-space-between">
        <v-card-title class="pa-0 mb-2 ml-2">
          {{ $t('eventTypes') }}
        </v-card-title>
        <v-col align-self="center" class="pa-0 te d-flex justify-end">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="plus-icon mr-3" v-on="on" v-bind="attrs"
                     @click="openModal">
                <v-icon color="grey" large>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('add') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="plus-icon mr-3" v-on="on" v-bind="attrs"
                     @click="onCloseList">
                <v-icon color="grey" large>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('close') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-divider class="mt-3"/>
      <v-sheet style="background-color: #ffffff">
        <v-list>
          <div v-if="eventTypeList">
            <event-type-row
              v-for="eventType in eventTypeList" :key="eventType.guid" :eventType="eventType"
              @edit="onEdit(eventType)" @delete="onDelete(eventType)">
            </event-type-row>
          </div>
        </v-list>
      </v-sheet>
    </v-card>
    <event-type-create-edit-modal
      :visible="createEventTypeModal"
      @confirm="onConfirmEventType"
      @cancel="onCloseModal"
      :eventType="eventTypeToCreate"
      type="create"
    />
    <event-type-create-edit-modal
      :visible="editEventTypeModal"
      @confirm="onEditEventType"
      @cancel="onCloseModal"
      :eventType="eventType"
      type="edit"
    />
    <dynameat-confirmation-modal :visible="eventTypeDeleteModal"
                                 :title="$t('deleteEventTypeTitle', [eventTypeToDeleteTitle])"
                                 :description="$t('deleteEventTypeDescription')"
                                 :confirm-text="$t('delete')" @confirm="onDeleteEventType"
                                 @cancel="onCloseModal"/>
  </v-dialog>
</template>

<script>
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { EventType } from '@eventManager/models';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import EventTypeRow from './EventTypeRow.vue';
import EventTypeCreateEditModal from './EventTypeCreateEditModal.vue';

@Component
@Components({
  EventTypeRow,
  EventTypeCreateEditModal,
  DynameatConfirmationModal
})
@MapGetters(['eventTypeList', 'createEventTypeSuccess', 'updateEventTypeSuccess',
  'deleteEventTypeSuccess'])
@MapActions(['eventTypeListRequest', 'createEventTypeRequest', 'updateEventTypeRequest',
  'deleteEventTypeRequest', 'eventListRequest'])
export default class EventTypeListModal extends Vue {
  @Prop({ Boolean, default: false }) visible;

  createEventTypeModal = false;

  editEventTypeModal = false;

  eventTypeDeleteModal = false;

  eventType = null;

  eventTypeToCreate = null;

  eventTypeToDelete = null;

  get eventTypeToDeleteTitle() {
    return this.eventTypeToDelete ? this.eventTypeToDelete.name : '';
  }

  onCloseList() {
    this.$emit('cancel');
  }

  onCloseModal() {
    this.createEventTypeModal = false;
    this.editEventTypeModal = false;
    this.eventTypeDeleteModal = false;
  }

  onEdit(eventType) {
    const props = eventType.serialize();
    this.eventType = EventType.toInstance(props);
    this.editEventTypeModal = true;
  }

  onDelete(eventType) {
    this.eventTypeToDelete = eventType;
    this.eventTypeDeleteModal = true;
  }

  openModal() {
    this.eventTypeToCreate = EventType.toInstance();
    this.createEventTypeModal = true;
  }

  async onConfirmEventType(eventType) {
    try {
      await this.createEventTypeRequest(eventType);
      this.addNotification(createSuccessNotification(this.$t('createEventTypeSuccess', [eventType.name]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('createEventTypeError', [eventType.name])));
    } finally {
      this.onCloseModal();
      this.eventTypeToCreate = null;
    }
  }

  async onEditEventType(eventType) {
    try {
      await this.updateEventTypeRequest(eventType);
      this.addNotification(createSuccessNotification(this.$t('editEventTypeSuccess', [eventType.name]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('editEventTypeError', [eventType.name])));
    } finally {
      this.onCloseModal();
      this.eventType = null;
    }
  }

  async onDeleteEventType() {
    try {
      await this.deleteEventTypeRequest(this.eventTypeToDelete);
      this.addNotification(createSuccessNotification(this.$t('deleteEventTypeSuccess', [this.eventTypeToDelete.name]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('deleteEventTypeError', [this.eventTypeToDelete.name])));
    } finally {
      this.onCloseModal();
      this.eventTypeToDelete = null;
    }
  }

  @Watch('createEventTypeSuccess')
  async onCreateEventTypeSuccessChange(newValue) {
    if (newValue) {
      await this.eventTypeListRequest();
    }
  }

  @Watch('updateEventTypeSuccess')
  async onUpdateEventTypeSuccessChange(newValue) {
    if (newValue) {
      await this.eventTypeListRequest();
      await this.eventListRequest();
    }
  }

  @Watch('deleteEventTypeSuccess')
  async onDeleteEventTypeSuccessChange(newValue) {
    if (newValue) {
      await this.eventTypeListRequest();
      await this.eventListRequest();
    }
  }
}
</script>

<style scoped>

</style>
